import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { toPromoZmSearch } from '@/features/promotion/utils/promotionFilters';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { MpReportSearch } from '../types/marketPromotion.types';
import {
  toMpReportFilterSearch,
  toMpReportFilterValues,
  type MpReportFilterSearch,
} from '../utils/mpFilters';

const adjustmentRoute = getRouteApi('/_auth/market-promotion/adjustment/');
const summaryRoute = getRouteApi('/_auth/market-promotion/distribution-summary');

function useMpReportFilters<TSearch extends MpReportSearch>(
  search: TSearch,
  applyFilterSearch: (filterSearch: MpReportFilterSearch) => void,
) {
  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(
    () => toMpReportFilterValues(search, defaultPeriod),
    [search, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: Record<string, string>) =>
      toMpReportFilterValues(toMpReportFilterSearch(values), defaultPeriod),
    [defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => applyFilterSearch(toMpReportFilterSearch(values)),
  });

  const params = useMemo(
    (): TSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  return { ...filters, params };
}

export function useMpAdjustmentFilters() {
  const search = adjustmentRoute.useSearch();
  const navigate = adjustmentRoute.useNavigate();

  const filters = useMpReportFilters(search, (filterSearch) => {
    void navigate({
      search: (previous) => ({ ...previous, ...filterSearch }),
      replace: true,
    });
  });

  const drillParams = useMemo(() => toPromoZmSearch(filters.params), [filters.params]);

  return { ...filters, drillParams };
}

export function useMpSummaryFilters() {
  const search = summaryRoute.useSearch();
  const navigate = summaryRoute.useNavigate();

  return useMpReportFilters(search, (filterSearch) => {
    void navigate({
      search: (previous) => ({ ...previous, ...filterSearch }),
      replace: true,
    });
  });
}
